// The Announcements feed (spec: 07-notifications.md).
//
// RENAMED IN ROUND 37, and only the name moved. This is the human feed —
// somebody writes a post, a crew acknowledges it — and it was called
// Notifications until Dayboard grew a second, system-generated feed beside
// it. Zanetto Tools carries both and settled the naming the same way: what a
// person writes is an announcement, and what the software noticed is a
// notification. The API stays `/api/posts` (the phone app reads it); the web
// route is /app/announcements, with the old detail URL redirecting.
//
// One screen, two audiences. A manager sees the compose box and the seen /
// acknowledged counts; a field worker sees the stream with an Acknowledge
// button on anything still owed. The server decides which is which — the role
// checks here are about not showing someone a control that would only fail.
//
// Phase 7 stage 4 restyled this against the ported design system. THE
// SUBSTANCE DID NOT MOVE: audience targeting, the acknowledgement trail,
// comments and the seen receipt are exactly as phase 4 built them, including
// the two audience rules Nathan decided on 2026-08-18 (everyone means active
// users only, and the author is not in their own audience). Those live in the
// server's audience.ts and changing either is a spec decision.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api';
import { nameOf, notifications, type Person, type Post } from '../notificationsApi';
import { useSession } from '../session';
import { EmptyState, FileDrop, PageHeader } from '../components/ui';

export function AnnouncementsPage() {
  const { user } = useSession();
  const [posts, setPosts] = useState<Post[] | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [error, setError] = useState<string | null>(null);

  const isManager = user?.role === 'manager' || user?.role === 'admin';

  const load = useCallback(async () => {
    try {
      const result = await notifications.feed();
      setPosts(result.posts);
      setPeople(result.people);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the announcements.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function acknowledge(post: Post) {
    try {
      await notifications.acknowledge(post.id);
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record that.');
    }
  }

  const owedCount =
    posts?.filter((p) => p.acknowledgement !== null && p.viewer.inAudience && !p.viewer.acknowledged)
      .length ?? 0;

  return (
    <>
      <PageHeader
        title="Announcements"
        subtitle={
          owedCount > 0
            ? `${owedCount} ${owedCount === 1 ? 'post needs' : 'posts need'} your acknowledgement.`
            : 'Safety alerts, weather calls and toolbox notes.'
        }
      />

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}

      {isManager && <Compose onPosted={load} onError={setError} />}

      {posts === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : posts.length === 0 ? (
        <EmptyState
          title="Nothing posted yet"
          body={
            isManager
              ? 'Use the box above to send the crew a safety alert or a toolbox note.'
              : 'Anything the office posts will show up here.'
          }
        />
      ) : (
        <ul className="space-y-3">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              people={people}
              isManager={isManager}
              onAcknowledge={() => void acknowledge(post)}
              onSeen={load}
            />
          ))}
        </ul>
      )}
    </>
  );
}

/** The poster's initial. Cheap identity cue so a feed of text has some shape. */
function Initial({ name }: { name: string }) {
  return (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-700"
    >
      {name.trim().charAt(0).toUpperCase() || '?'}
    </span>
  );
}

/**
 * One post in the feed.
 *
 * The seen receipt is reported when the card actually reaches the screen, not
 * when the feed loads — "seen" in the spec means the post was rendered where
 * somebody could read it, and a card sitting forty posts down the page has not
 * been. IntersectionObserver is the honest version of that sentence.
 */
function PostCard({
  post,
  people,
  isManager,
  onAcknowledge,
  onSeen,
}: {
  post: Post;
  people: Person[];
  isManager: boolean;
  onAcknowledge: () => void;
  onSeen: () => void;
}) {
  const ref = useRef<HTMLLIElement | null>(null);
  const reported = useRef(false);

  useEffect(() => {
    if (post.viewer.seen || reported.current) return;
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting || reported.current) continue;
        reported.current = true;
        observer.disconnect();
        // Fire and forget: a failed receipt is not worth interrupting someone
        // reading a safety notice, and the next page load will try again.
        void notifications.markSeen(post.id).then(onSeen, () => undefined);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [post.id, post.viewer.seen, onSeen]);

  const owed = post.acknowledgement !== null && post.viewer.inAudience && !post.viewer.acknowledged;
  const author = nameOf(people, post.authorId);

  return (
    <li
      ref={ref}
      // The red stripe is the same language a conflict uses on the schedule:
      // both mean "this one needs you".
      className={`card p-4 ${owed ? 'border-l-4 border-l-red-500' : ''}`}
    >
      <div className="flex items-start gap-3">
        <Initial name={author} />

        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-sm font-semibold text-gray-900">{author}</span>
            <span className="text-xs text-gray-600">{when(post.createdAt)}</span>
            {post.edited && <span className="text-xs text-gray-600">edited</span>}
            {post.audienceMode === 'picked' && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[0.65rem] font-medium text-blue-700 ring-1 ring-inset ring-blue-600/25">
                Picked people
              </span>
            )}
            {post.requiresAck && (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-[0.65rem] font-medium text-red-700 ring-1 ring-inset ring-red-600/25">
                Needs acknowledgement
              </span>
            )}
          </div>

          <p className="whitespace-pre-wrap text-sm text-gray-800 [overflow-wrap:anywhere]">
            {post.body}
          </p>

          {post.job && (
            <p className="mt-2 text-xs text-gray-600">
              About{' '}
              {isManager ? (
                <Link to={`/app/jobs/${post.job.id}`} className="table-link">
                  {post.job.number} · {post.job.title}
                </Link>
              ) : (
                <>
                  {post.job.number} · {post.job.title}
                </>
              )}
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            {owed && (
              <button type="button" onClick={onAcknowledge} className="btn-primary btn-sm">
                Acknowledge
              </button>
            )}
            {post.viewer.acknowledged && (
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800 ring-1 ring-inset ring-green-600/25">
                You acknowledged this
              </span>
            )}

            <Link to={`/app/announcements/${post.id}`} className="row-action">
              {post.commentCount === 0
                ? 'Open'
                : `${post.commentCount} comment${post.commentCount === 1 ? '' : 's'}`}
            </Link>

            {/* A post with files looked identical to one without until you
                opened it (round 38) — which matters most on exactly the post
                whose body says "drawing attached". */}
            {post.attachmentCount > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                  />
                </svg>
                {post.attachmentCount} file{post.attachmentCount === 1 ? '' : 's'}
              </span>
            )}

            {isManager && (
              <span className="ml-auto text-xs text-gray-600">
                Seen by {post.seenCount}
                {post.acknowledgement &&
                  ` · acknowledged ${post.acknowledgement.acknowledgedCount} of ${post.acknowledgement.audienceSize}`}
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

/** The compose box: body, audience, acknowledgement toggle, optional job. */
function Compose({
  onPosted,
  onError,
}: {
  onPosted: () => void;
  onError: (message: string | null) => void;
}) {
  const [body, setBody] = useState('');
  const [requiresAck, setRequiresAck] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [choosing, setChoosing] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!choosing || people.length > 0) return;
    void notifications.people().then(
      (result) => setPeople(result.people),
      () => onError('Could not load the list of people.')
    );
  }, [choosing, people.length, onError]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (body.trim() === '' || busy) return;

    setBusy(true);
    setProgress(files.length > 0 ? 0 : null);
    try {
      await notifications.create(
        {
          body: body.trim(),
          audienceMode: choosing ? 'picked' : 'everyone',
          audienceUserIds: choosing ? picked : [],
          requiresAck,
          files,
        },
        (fraction) => setProgress(fraction)
      );
      setBody('');
      setRequiresAck(false);
      setPicked([]);
      setChoosing(false);
      setFiles([]);
      onError(null);
      onPosted();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not post that.');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  /** Adds to what is already chosen, so two trips to the picker both count. */
  function addFiles(chosen: File[]) {
    if (chosen.length === 0) return;
    // FileDrop clears its input after every pick, so the same file chosen
    // twice in a row still fires.
    setFiles((current) => [...current, ...chosen].slice(0, MAX_FILES));
  }

  return (
    <form className="card mb-5 p-4" onSubmit={(event) => void submit(event)}>
      <label className="form-label" htmlFor="post-body">
        Post to the crew
      </label>
      <textarea
        id="post-body"
        rows={3}
        className="form-textarea"
        value={body}
        placeholder="Safety alert, weather call, toolbox note…"
        onChange={(event) => setBody(event.target.value)}
      />

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
        <label className="check-label">
          <input
            type="checkbox"
            className="form-check"
            checked={requiresAck}
            onChange={(event) => setRequiresAck(event.target.checked)}
          />
          Everyone must acknowledge this
        </label>

        <label className="check-label">
          <input
            type="checkbox"
            className="form-check"
            checked={choosing}
            onChange={(event) => setChoosing(event.target.checked)}
          />
          Send to picked people only
        </label>
      </div>

      {choosing && (
        <fieldset className="mt-3 max-h-56 overflow-y-auto rounded-md border border-gray-200 px-4 py-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Who is this for?
          </legend>
          {people.length === 0 ? (
            <p className="text-sm text-gray-500">Loading people…</p>
          ) : (
            <div className="space-y-1.5">
              {people.map((person) => (
                <label key={person.id} className="check-label">
                  <input
                    type="checkbox"
                    className="form-check"
                    checked={picked.includes(person.id)}
                    onChange={(event) =>
                      setPicked((current) =>
                        event.target.checked
                          ? [...current, person.id]
                          : current.filter((id) => id !== person.id)
                      )
                    }
                  />
                  {person.name}
                </label>
              ))}
            </div>
          )}
        </fieldset>
      )}

      {files.length > 0 && (
        <ul className="mt-3 flex list-none flex-wrap gap-2 p-0">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 py-1 pl-1 pr-2"
            >
              <FileThumb file={file} />
              <span className="max-w-[12rem] truncate text-xs text-gray-700" title={file.name}>
                {file.name}
              </span>
              <span className="text-[0.65rem] text-gray-500">{fileSize(file.size)}</span>
              <button
                type="button"
                // 28px DRAWN, 44px TOUCHED. A ✕ the size of a real button
                // makes the chip the size of a button, but a 28px target on a
                // phone is under the floor the whole app holds to — so the
                // pseudo-element grows the hit area 8px in every direction
                // without taking any layout space. The chips' own `gap-2`
                // means two of these still cannot overlap.
                className="relative flex h-7 w-7 flex-none items-center justify-center rounded-full text-gray-500 transition-colors after:absolute after:-inset-2 after:content-[''] hover:bg-gray-200 hover:text-gray-800"
                aria-label={`Remove ${file.name}`}
                disabled={busy}
                onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
        {/* Never a bare <input type="file"> (round 33); and a file dropped
            on the button attaches like a chosen one (round 45). */}
        <FileDrop
          variant="button"
          id="post-files"
          multiple
          accept={ACCEPT}
          typeLabel="PDF, images, Word and Excel"
          buttonClassName="btn-secondary btn-sm mr-auto"
          disabled={busy || files.length >= MAX_FILES}
          label={files.length === 0 ? 'Attach files' : 'Add another file'}
          onFiles={addFiles}
          onReject={onError}
        />

        {progress !== null && (
          <span role="status" className="text-xs text-gray-600">
            Uploading… {Math.round(progress * 100)}%
          </span>
        )}

        <button type="submit" disabled={busy || body.trim() === ''} className="btn-primary btn-sm">
          {busy ? 'Posting…' : 'Post'}
        </button>
      </div>

      {files.length >= MAX_FILES && (
        <p className="!mb-0 mt-2 text-xs text-gray-600">
          That is the {MAX_FILES}-file limit for one post. Anything more can go on the post after
          it is up.
        </p>
      )}
    </form>
  );
}

/** The same limit the server enforces (`MAX_POST_FILES` in routes/posts.ts). */
const MAX_FILES = 10;

/** The attachments allowlist, as the picker's filter. */
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx';

function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * A thumbnail for an image, a paperclip for everything else.
 *
 * The object URL is revoked when the chip goes, because a composer somebody
 * picks twenty photos through and thinks better of would otherwise hold all
 * twenty in memory until the page is left.
 */
function FileThumb({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file.type.startsWith('image/')) return;
    const made = URL.createObjectURL(file);
    setUrl(made);
    return () => {
      URL.revokeObjectURL(made);
      setUrl(null);
    };
  }, [file]);

  if (url) {
    return <img src={url} alt="" className="h-8 w-8 flex-none rounded object-cover" />;
  }

  return (
    <span className="flex h-8 w-8 flex-none items-center justify-center rounded bg-gray-200 text-gray-600">
      <svg
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.8}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
        />
      </svg>
    </span>
  );
}

/** Dates in the feed read better as "today" than as a timestamp. */
function when(iso: string): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  const today = new Date();
  const sameDay =
    at.getFullYear() === today.getFullYear() &&
    at.getMonth() === today.getMonth() &&
    at.getDate() === today.getDate();

  return sameDay ? `today, ${time}` : `${at.toLocaleDateString('en-AU')}, ${time}`;
}
